<script lang="ts" setup>
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { computed, onMounted, reactive, ref, Ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { QuestionMarkCircleIcon } from '@heroicons/vue/outline';

import { goBack } from '@/composables/useNavigation';
import { NumberService, SelectOptionsService, WhatsappCodeService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { useUiStore, useAuthStore } from '@/stores';
import { applyPhoneMask } from '@/utils/number';

import {
    DefaultServiceResult,
    NumberFormState,
    NumberModel,
    Permission,
    SelectOptionsServiceGetLevelsResult,
} from '@/types';
import SVGWhatsApp from '@/components/svg/SVGWhatsApp.vue';

const formState = reactive<NumberFormState>({
    levelId: undefined,
    connectFlowId: undefined,
    mediaLabel: undefined,
});
const number = ref<NumberModel>();
const levelOptions: Ref<SelectOptionsServiceGetLevelsResult[]> = ref([]);
const connectFlowOptions: Ref<Array<{ id: number; name: string }>> = ref([]);
const receiveWhatsappCode = ref(false);
const isInitialLoad = ref(true);

const rules = computed(() => ({
    levelId: { required },
    connectFlowId: {},
    mediaLabel: { maxLength: maxLength(250) },
}));

const route = useRoute();
const uiStore = useUiStore();
const authStore = useAuthStore();
const v$: Ref<Validation<ValidationArgs<NumberFormState>>> = useVuelidate(rules, formState);
const canAssignWhatsappCode = computed(() =>
    authStore.userPermissions.includes(Permission.ASSIGN_WHATSAPP_CODE_NUMBER),
);

watch(
    () => formState.levelId,
    async (newVal, oldVal) => {
        if (!isInitialLoad.value && newVal !== oldVal) {
            formState.connectFlowId = null;
            receiveWhatsappCode.value = false;
        }
        await fetchConnectFlows();
    },
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchNumber = async (id: number) => {
    try {
        setLoading(true);
        const { data } = await NumberService.get<NumberModel>(id);
        number.value = data;
        formState.levelId = data.level.id;
        receiveWhatsappCode.value = data.whatsappRouting?.isActive === true;
        formState.connectFlowId = data.whatsappRouting?.isActive ? null : data.connectFlowId;
        formState.mediaLabel = data.mediaLabel;
    } catch (err) {
        toastServiceError(err);
    } finally {
        setLoading(false);
    }
};

const fetchLevels = async () => {
    try {
        const { data } = await SelectOptionsService.getLevels();
        levelOptions.value = data;
    } catch (err) {
        toastServiceError(err);
    }
};

const fetchConnectFlows = async () => {
    if (formState.levelId) {
        try {
            const { data } = await SelectOptionsService.getConnectFlows({
                params: {
                    level: formState.levelId,
                },
            });
            connectFlowOptions.value = [...data];
        } catch (err) {
            toastServiceError(err);
        }
    }
};

const onSubmit = async () => {
    const isFormCorrect = await v$.value.$validate();
    if (!isFormCorrect) return;

    if (formState.mediaLabel === '') {
        formState.mediaLabel = null;
    }

    try {
        setLoading(true);
        const id = number.value?.id as number;

        if (receiveWhatsappCode.value) {
            await WhatsappCodeService.activateRouting(id);
            const { levelId, mediaLabel } = formState;
            const { data } = await NumberService.update<DefaultServiceResult>(id, {
                levelId,
                mediaLabel,
            });
            toast.success(data.message);
        } else {
            const { data } = await NumberService.update<DefaultServiceResult>(id, formState);
            toast.success(data.message);
        }

        v$.value.$reset();
        goBack({ name: 'ViewNumberById', params: { id } });
    } catch (err) {
        toastServiceError(err);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    await fetchNumber(Number(route.params.id));
    await fetchLevels();

    if (formState.levelId) {
        await fetchConnectFlows();
    }

    isInitialLoad.value = false;
});
</script>

<template>
    <form class="space-y-4 p-4 sm:p-6 lg:p-8" autocomplete="off" @submit.prevent="onSubmit">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('number.edit.title') }}
                </h1>
            </template>
            <template #actions>
                <div class="flex items-center space-x-4">
                    <UiBackButton :fallbackTo="{ name: 'ListNumber' }" />
                    <UiButton
                        v-if="$can(Permission.EDIT_NUMBER)"
                        type="submit"
                        variant="primary"
                        text-variant="white"
                    >
                        {{ $t('core.actions.Save') }}
                    </UiButton>
                </div>
            </template>
        </UiPageHeader>

        <UiPanel>
            <div v-if="number" class="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div>
                    <label class="mb-2 block text-sm font-medium text-gray-800">
                        {{ $t('core.entity.Level') }}
                    </label>
                    <UiMultiselect
                        v-model="formState.levelId"
                        name="level"
                        label="name"
                        value-prop="id"
                        primitive
                        :options="levelOptions"
                    />
                </div>
                <UiTextInput
                    :model-value="number ? applyPhoneMask(number.number) : ''"
                    name="mediaLabel"
                    type="text"
                    :label="$t('number.labels.number')"
                    disabled
                />
                <UiTextInput
                    :model-value="`${number?.city} / ${number?.state}`"
                    name="mediaLabel"
                    type="text"
                    :label="$t('number.labels.city')"
                    disabled
                />
                <UiTextInput
                    :model-value="$t(`number.types.${number?.type}`)"
                    name="mediaLabel"
                    type="text"
                    :label="$t('number.labels.type')"
                    disabled
                />
                <div>
                    <label class="mb-2 block text-sm font-medium text-gray-800">
                        {{ $t('core.entity.ConnectFlow') }}
                    </label>
                    <UiMultiselect
                        v-model="formState.connectFlowId"
                        name="connectFlow"
                        label="name"
                        value-prop="id"
                        primitive
                        :options="connectFlowOptions"
                        can-clear
                        :disabled="receiveWhatsappCode"
                    />
                    <div class="mt-3 flex items-center gap-2">
                        <UiCheckboxInput
                            v-model="receiveWhatsappCode"
                            name="receiveWhatsappCode"
                            :label="$t('number.labels.whatsappRoutingOption')"
                            :disabled="!canAssignWhatsappCode"
                        >
                            <template #icon>
                                <SVGWhatsApp class="h-4 w-4 text-[#25D366]" />
                            </template>
                        </UiCheckboxInput>
                        <span
                            v-tippy="{ content: $t('number.tooltips.whatsappRoutingOption') }"
                            class="text-gray-400 transition-colors duration-150 hover:text-gray-600"
                        >
                            <QuestionMarkCircleIcon
                                class="h-4 w-4 cursor-help"
                                aria-hidden="true"
                            />
                        </span>
                    </div>
                </div>
                <UiTextInput
                    v-model="formState.mediaLabel"
                    name="mediaLabel"
                    type="text"
                    :label="$t('number.labels.mediaLabel')"
                    :errors="v$.mediaLabel?.$errors"
                    @blur="v$.mediaLabel?.$touch"
                />
            </div>
        </UiPanel>
        <UiCheckDirtyBeforeRouteLeave :dirty="v$.$anyDirty" />
    </form>
</template>
